<template>
  <div>
    <b-form-row>
      <div v-if="smallTitle" class="col-md-4 text-primary">{{addressType}} Address</div>
      <h5 v-else class="col-md-4 text-primary font-weight-bold pb-2">{{addressType}} Address</h5>
      <slot name="header"></slot>
    </b-form-row>
    <ValidationProvider
      :name="`${addressType} Country`"
      :rules="{ required: !disabled }"
      v-slot="{ errors, classes }"
    >
      <b-form-group
        label-cols-lg="2"
        :label-for="`${addressSelector}-country`"
      >
        <template v-slot:label><span class="req-ind">*</span>Country</template>
        <select
          :id="`${addressSelector}-country`"
          :name="`${addressSelector}-country`"
          v-model="address.countryId"
          class="form-control"
          :class="classes"
          :disabled="isDisabled"
        >
          <option v-for="option in countryOptions" :key="option.id" :value="option.id">
            {{option.name}}
          </option>
        </select>
        <InputErrors :errors="errors" :errorsIdPrefix="`${addressSelector}-country`" />
      </b-form-group>
    </ValidationProvider>
    <ValidationProvider
      :name="`${addressType} Street Address Line 1`"
      :rules="{ required: !disabled }"
      v-slot="{ errors, classes }"
    >
      <b-form-group
        label-cols-lg="2"
        :label-for="`${addressSelector}-street1`"
      >
        <template v-slot:label>
          <span class="req-ind">*</span>Street Address Line 1
        </template>
        <b-form-input
          :id="`${addressSelector}-street1`"
          :name="`${addressSelector}-street1`"
          v-model="address.street1"
          placeholder="Enter Street Address"
          :class="classes"
          :disabled="isDisabled"
          :aria-describedby="$errorsIdString(errors, `${addressSelector}-street1`)"
        />
        <InputErrors :errors="errors" :errorsIdPrefix="`${addressSelector}-street1`" />
      </b-form-group>
    </ValidationProvider>
    <b-form-group
      label-cols-lg="2"
      label="Street Address Line 2"
      :label-for="`${addressSelector}-street2`"
    >
      <b-form-input
        :id="`${addressSelector}-street2`"
        :name="`${addressSelector}-street2`"
        v-model="address.street2"
        placeholder="Enter Street Address"
        :disabled="isDisabled"
      />
    </b-form-group>
    <b-form-row>
      <b-col cols="12" lg="4">
        <ValidationProvider
          :name="`${addressType} City`"
          :rules="{ required: !disabled }"
          v-slot="{ errors, classes }"
        >
          <b-form-group :label-for="`${addressSelector}-city`">
            <template v-slot:label><span class="req-ind">*</span>City</template>
            <b-form-input
              :id="`${addressSelector}-city`"
              :name="`${addressSelector}-city`"
              v-model="address.city"
              placeholder="Enter City"
              :class="classes"
              :disabled="isDisabled"
              :aria-describedby="$errorsIdString(errors, `${addressSelector}-city`)"
            />
            <InputErrors :errors="errors" :errorsIdPrefix="`${addressSelector}-city`" />
          </b-form-group>
        </ValidationProvider>
      </b-col>
      <template v-if="isUSAddress">
        <b-col cols="12" lg="4">
          <ValidationProvider
            :name="`${addressType} State`"
            :rules="{ required: !disabled }"
            v-slot="{ errors, classes }"
          >
            <b-form-group :label-for="`${addressSelector}-state`">
              <template v-slot:label><span class="req-ind">*</span>State</template>
              <select
                :id="`${addressSelector}-state`"
                :name="`${addressSelector}-state`"
                v-model="address.stateId"
                class="form-control"
                :class="classes"
                :disabled="isDisabled"
                :aria-describedby="$errorsIdString(errors, `${addressSelector}-state`)"
              >
                <option v-for="option in stateOptions" :key="option.id" :value="option.id">
                  {{option.name}}
                </option>
              </select>
              <InputErrors :errors="errors" :errorsIdPrefix="`${addressSelector}-state`" />
            </b-form-group>
          </ValidationProvider>
        </b-col>
        <b-col cols="12" lg="4">
          <ValidationProvider
            :name="`${addressType} Zip`"
            :rules="{ required: !disabled, regex: /^[0-9]{5}(-[0-9]{4})?$/ }"
            v-slot="{ errors, classes }"
          >
            <b-form-group :label-for="`${addressSelector}-zip`">
              <template v-slot:label><span class="req-ind">*</span>Zip</template>
              <b-form-input
                :id="`${addressSelector}-zip`"
                :name="`${addressSelector}-zip`"
                v-model="address.zip"
                placeholder="Enter Zip"
                maxlength="10"
                :class="classes"
                :disabled="isDisabled"
                :aria-describedby="$errorsIdString(errors, `${addressSelector}-zip`)"
              />
              <InputErrors :errors="errors" :errorsIdPrefix="`${addressSelector}-zip`" />
            </b-form-group>
          </ValidationProvider>
        </b-col>
      </template>
      <template v-else>
        <b-col cols="12" lg="4">
          <b-form-group
            v-if="!isUSAddress"
            label="State/Province/Region"
            :label-for="`${addressSelector}-stateProvinceRegion`"
          >
            <b-form-input
              :id="`${addressSelector}-stateProvinceRegion`"
              :name="`${addressSelector}-stateProvinceRegion`"
              v-model="address.stateProvinceRegion"
              placeholder="Enter State/Province/Region"
              :disabled="isDisabled"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12" lg="4">
          <b-form-group
            label="Zip/Postal Code/Postcode"
            :label-for="`${addressSelector}-zipPostalcodePostcode`"
          >
            <b-form-input
              :id="`${addressSelector}-zipPostalcodePostcode`"
              :name="`${addressSelector}-zipPostalcodePostcode`"
              v-model="address.zipPostalcodePostcode"
              placeholder="Enter Zip/Postal Code/Postcode"
              :disabled="isDisabled"
            />
          </b-form-group>
        </b-col>
      </template>
    </b-form-row>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import LookupModules from '@/store/moduleConfig';

export default {
  name: 'AddressForm',
  props: {
    address: {
      type: Object,
      required: true,
    },
    addressType: {
      type: String,
      required: true,
    },
    addressSelector: {
      type: String,
      required: true,
    },
    smallTitle: {
      type: Boolean,
      default: false,
    },
    isAdminEdit: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
  },
  created() {
    this.$store.dispatch(`${LookupModules.COUNTRIES.namespace}/fetchOptions`);
    this.$store.dispatch(`${LookupModules.STATES.namespace}/fetchOptions`);
  },
  watch: {
    // Unset the certification method whenever the user unchecks licensedForester additional role
    // eslint-disable-next-line object-shorthand
    'address.countryId'(cId) {
      const merica = cId === 840;
      if (merica) {
        this.address.stateProvinceRegion = '';
        this.address.zipPostalcodePostcode = '';
      } else {
        this.address.stateId = '';
        this.address.zip = '';
      }
    },
  },
  computed: {
    isDisabled() {
      if (!this.isAdminEdit) {
        return this.mfsAdmin || this.disabled;
      }

      return this.disabled;
    },
    ...mapState(LookupModules.COUNTRIES.namespace, {
      countryOptions: state => state.options,
      countryOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.STATES.namespace, {
      stateOptions: state => state.options,
      stateOptionsLoading: state => state.loading,
    }),
    ...mapGetters('User', ['mfsAdmin']),
    isUSAddress() {
      return this.address.countryId === 840;
    },
  },
};
</script>
